import React, {useMemo} from "react";
import {Grid, Skeleton} from "@mui/material";
import {BrightnessLowOutlined, CheckCircleOutlined, EnergySavingsLeafOutlined, SsidChartOutlined} from "@mui/icons-material";
import {AProgressTotalizer} from "@atiautomacao/ati-ui-library";
import {differenceInCalendarDays} from "date-fns";
import {usePerformanceTotalizersQuery} from "./reactQuery/PerformanceTotalizersReactQuery";
import {PerformanceTotalizer} from "./PerformanceTotalizer.types";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {canCalculatePercentage, formatMeasuredValue, hasExpectedData, NO_DATA} from "../utils/PerformanceDisplayUtils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";

const GENERATED_ENERGY_NAME = "Energia Gerada";
const IRRADIANCE_NAME = "Irradiação";
const PERFORMANCE_RATIO_NAME = "PR Médio";
const AVAILABILITY_NAME = "Disponibilidade";
// Limiar do AProgressTotalizer: percentuais com módulo >= a este valor são tratados como
// implausíveis e exibidos como "- %" — não é um teto para a barra de progresso.
const PERCENTAGE_HIDE_THRESHOLD = 150;

interface PerformanceTotalizersProps {
    powerStationIds: number[];
    startDateTime: string | null;
    endDateTime: string | null;
}

interface TotalizerCardDefinition {
    key: string;
    label: string;
    unit: string;
    icon: React.ElementType;
}

interface TotalizerCard extends TotalizerCardDefinition {
    value: number | string;
    target?: number | string;
    progressValue?: number;
    progressVariant?: "linear";
    showPercentage?: boolean;
    limitMaxPercentage?: number;
}

const findByName = (totalizers: PerformanceTotalizer[] | undefined, name: string): PerformanceTotalizer | undefined =>
    totalizers?.find((totalizer) => totalizer.name === name);

/**
 * SGD-3680: apenas a ausência de dado esperado é exibida como "-" e "- %". Um valor
 * esperado igual a zero continua sendo exibido como 0, mas sem percentual, porque a
 * razão medido/esperado não é calculável.
 */
const buildComparisonCard = (
    totalizer: PerformanceTotalizer | undefined,
    definition: TotalizerCardDefinition
): TotalizerCard => {
    return {
        ...definition,
        value: formatMeasuredValue(totalizer?.generatedValue),
        target: hasExpectedData(totalizer?.expectedValue)
            ? (truncateNumber(totalizer?.expectedValue, 2) ?? NO_DATA)
            : NO_DATA,
        // undefined faz o AProgressTotalizer renderizar "- %"
        progressValue: canCalculatePercentage(totalizer?.expectedValue)
            ? (truncateNumber(totalizer?.associationPercentage, 2) ?? undefined)
            : undefined,
        // Explícitos por necessidade: sem eles, um progressValue undefined faria o
        // AProgressTotalizer cair na variante "none" e esconder barra e percentual.
        progressVariant: "linear",
        showPercentage: true,
        limitMaxPercentage: PERCENTAGE_HIDE_THRESHOLD,
    };
};

export default function PerformanceTotalizers({powerStationIds, startDateTime, endDateTime}: PerformanceTotalizersProps) {
    const {data, isLoading, isError, consecutiveAutomaticFailures, maxPreservedRefreshFailures, hasValidSnapshot, isEmptySuccess} =
        usePerformanceTotalizersQuery(powerStationIds, startDateTime, endDateTime);
    // Skeleton only on cold load — keep previous values during filter/period refetch (FR-014)
    const showSkeleton = isLoading && !hasValidSnapshot && !isEmptySuccess;
    const preserveExpired = consecutiveAutomaticFailures > maxPreservedRefreshFailures;
    const showDataNotFound =
        preserveExpired || isEmptySuccess || (isError && !hasValidSnapshot);

    const generatedEnergy = findByName(data, GENERATED_ENERGY_NAME);
    const irradiance = findByName(data, IRRADIANCE_NAME);
    const performanceRatio = findByName(data, PERFORMANCE_RATIO_NAME);
    const availability = findByName(data, AVAILABILITY_NAME);
    const lgSize = useMemo(() => {
        return startDateTime && endDateTime && differenceInCalendarDays(new Date(endDateTime), new Date(startDateTime)) > 29 ? 6 : 3;
    }, [startDateTime, endDateTime]);
  

    const cards = useMemo<TotalizerCard[]>(
        () => [
            buildComparisonCard(generatedEnergy, {
                key: GENERATED_ENERGY_NAME,
                label: GENERATED_ENERGY_NAME,
                unit: "MWh",
                icon: EnergySavingsLeafOutlined,
            }),
            buildComparisonCard(irradiance, {
                key: IRRADIANCE_NAME,
                label: IRRADIANCE_NAME,
                unit: "kWh/m²",
                icon: BrightnessLowOutlined,
            }),
            buildComparisonCard(performanceRatio, {
                key: PERFORMANCE_RATIO_NAME,
                label: PERFORMANCE_RATIO_NAME,
                unit: "%",
                icon: SsidChartOutlined,
            }),
            {
                key: AVAILABILITY_NAME,
                label: AVAILABILITY_NAME,
                value: truncateNumber(availability?.generatedValue ?? 0, 2),
                progressValue: truncateNumber(availability?.associationPercentage ?? 0, 2) ?? undefined,
                unit: "%",
                icon: CheckCircleOutlined,
                showPercentage: false,
            },
        ],
        [generatedEnergy, irradiance, performanceRatio, availability]
    );

    if (showDataNotFound) {
        return (
            <Grid container sx={{paddingLeft: 3, paddingBottom: 2}}>
                <DataNotFound boxStyle={{height: "150px", width: "100%"}}/>
            </Grid>
        );
    }

    return (
        <Grid key={lgSize} container spacing={2} sx={{paddingLeft: 3, paddingRight: 3, paddingBottom: 2}}>
            {cards.map((card) => (
                <Grid item xs={12} md={6} lg={lgSize} xl={3} key={card.key + lgSize}>
                    {showSkeleton ? (
                        <Skeleton animation="wave" height={92} variant="rounded"/>
                    ) : (
                        <AProgressTotalizer
                            sx={{height: 75}}
                            label={card.label}
                            value={card.value}
                            target={card.target}
                            progressValue={card.progressValue}
                            progressVariant={card.progressVariant}
                            limitMaxPercentage={card.limitMaxPercentage}
                            showPercentage={card.showPercentage}
                            unit={card.unit}
                            icon={card.icon}
                        />
                    )}
                </Grid>
            ))}
        </Grid>
    );
}
